import { AnsweringClient, IInstrument } from "../../clients/answeringClient";

export interface IUser {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UserEntry {
  id: string;
  type: "existing" | "new";
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken?: string;
}

export interface CreateAssignmentModalControllerDelegate {
  didGetInstruments: (assignments: IInstrument[]) => void;
  didGetUsers: (users: IUser[]) => void;
  didCreateAssignment: (assignmentId: number, payload: { name: string; instrumentId: number; dueDate: Date }) => void;
  applicationShouldLogout: () => void;
  genericError: () => void;
}

export class CreateAssignmentModalController {
  constructor(
    private delegate: CreateAssignmentModalControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async searchInstruments(accessToken: string, filter?: string) {
    try {
      const response = await this.answeringClient.getManagedInstrumentsSearch(
        accessToken,
        { page: 1, limit: 20, filter: filter || undefined }
      );
      this.delegate.didGetInstruments(response.data.data);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.searchInstruments(newTokens.accessToken, filter);
        return;
      }
      this.delegate.genericError();
    }
  }

  public async searchUsers(accessToken: string, filter?: string): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedUsers(accessToken, {
        page: 1,
        limit: 15,
        filter: filter || undefined,
      });
      const data: any[] = response.data.data ?? [];
      const users: IUser[] = data
        .filter((u) => u.identity)
        .map((u) => ({
          uid: u.uid,
          firstName: u.identity.firstName,
          lastName: u.identity.lastName,
          email: u.identity.email,
        }));
      this.delegate.didGetUsers(users);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        this.searchUsers(newTokens.accessToken, filter);
        return;
      }
      this.delegate.didGetUsers([]);
    }
  }

  public async createAssignment(
    accessToken: string,
    dueDate: Date,
    instrumentId: number,
    name: string,
    allowFeedback: boolean = false
  ) {
    try {
      const response = await this.answeringClient.createAssignment(
        accessToken,
        dueDate,
        instrumentId,
        name,
        allowFeedback
      );
      const result = response.data as { id?: number } | undefined;
      const assignmentId = result?.id;
      this.delegate.didCreateAssignment(assignmentId ?? 0, {
        name,
        instrumentId,
        dueDate,
      });
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.createAssignment(
          newTokens.accessToken,
          dueDate,
          instrumentId,
          name,
          allowFeedback
        );
        return;
      }
      this.delegate.genericError();
    }
  }

  public async assignSingleUser(
    accessToken: string,
    user: UserEntry,
    assignmentId: number
  ): Promise<void> {
    if (user.type === "existing") {
      await this.answeringClient.addAssignmentsToUser(accessToken, user.id, [assignmentId]);
    } else {
      await this.answeringClient.inviteUser(
        accessToken,
        user.firstName,
        user.lastName,
        user.email,
        [assignmentId],
        user.recaptchaToken ?? ""
      );
    }
  }


  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
